/**
 * SeoDiagnosticsCard.tsx
 *
 * Tiered SEO & OpenGraph Diagnostics Engine with Schema.org JSON-LD Linter.
 * Severity levels: 'pass' | 'advisory' | 'warning' | 'error'.
 */

import React from 'react';
import type { Product, ProductVariant } from '../../plugins/commerce/commerce-contracts.js';
import type { MediaAsset } from '../../plugins/commerce/inventory/inventoryContracts.js';

export type DiagnosticSeverity = 'pass' | 'advisory' | 'warning' | 'error';

export interface SeoDiagnosticRule {
  readonly id: string;
  readonly label: string;
  readonly severity: DiagnosticSeverity;
  readonly message: string;
  readonly actualValue?: string | number;
}

export interface SeoDiagnosticsCardProps {
  readonly product: Partial<Product>;
  readonly variants: readonly Partial<ProductVariant>[];
  readonly media: readonly MediaAsset[];
  readonly locale: string;
}

export function evaluateSeoDiagnostics(
  product: Partial<Product>,
  variants: readonly Partial<ProductVariant>[],
  media: readonly MediaAsset[],
  locale = 'en-US'
): readonly SeoDiagnosticRule[] {
  const rules: SeoDiagnosticRule[] = [];

  const titleText =
    typeof product.title === 'string'
      ? product.title
      : product.title?.[locale] || Object.values(product.title || {})[0] || '';

  const descText =
    typeof product.description === 'string'
      ? product.description
      : product.description?.[locale] || Object.values(product.description || {})[0] || '';

  // 1. Title Length Rule (Advisory heuristic)
  const titleLen = titleText.trim().length;
  if (titleLen === 0) {
    rules.push({
      id: 'title-missing',
      label: 'Page Title',
      severity: 'error',
      message: 'Product title is empty.',
      actualValue: 0,
    });
  } else if (titleLen < 30) {
    rules.push({
      id: 'title-short',
      label: 'SEO Title Length',
      severity: 'advisory',
      message: 'Title is brief (<30 chars). Consider adding brand or key attributes.',
      actualValue: `${titleLen} chars`,
    });
  } else if (titleLen > 65) {
    rules.push({
      id: 'title-long',
      label: 'SEO Title Length',
      severity: 'advisory',
      message: 'Title exceeds 65 characters and may truncate on search result pages.',
      actualValue: `${titleLen} chars`,
    });
  } else {
    rules.push({
      id: 'title-optimal',
      label: 'SEO Title Length',
      severity: 'pass',
      message: 'Title length is well-balanced for search indexing.',
      actualValue: `${titleLen} chars`,
    });
  }

  // 2. Meta Description Length (Advisory heuristic)
  const descLen = descText.trim().length;
  if (descLen === 0) {
    rules.push({
      id: 'desc-missing',
      label: 'Meta Description',
      severity: 'warning',
      message: 'Missing product description. Search engines will auto-generate snippets.',
      actualValue: 0,
    });
  } else if (descLen < 80) {
    rules.push({
      id: 'desc-short',
      label: 'Meta Description',
      severity: 'advisory',
      message: 'Short description (<80 chars). Expanding may improve click-through rate.',
      actualValue: `${descLen} chars`,
    });
  } else if (descLen > 180) {
    rules.push({
      id: 'desc-long',
      label: 'Meta Description',
      severity: 'advisory',
      message: 'Description exceeds 180 characters; trailing text will be clipped.',
      actualValue: `${descLen} chars`,
    });
  } else {
    rules.push({
      id: 'desc-optimal',
      label: 'Meta Description',
      severity: 'pass',
      message: 'Meta description length is optimal for search snippets.',
      actualValue: `${descLen} chars`,
    });
  }

  // 3. Media & OpenGraph Image Rule
  const primaryImage = media.find((m) => m.role === 'primary') || media[0];
  if (!primaryImage) {
    rules.push({
      id: 'og-image-missing',
      label: 'OpenGraph Image',
      severity: 'warning',
      message: 'No primary image attached. Social media shares will render without preview cards.',
    });
  } else {
    rules.push({
      id: 'og-image-pass',
      label: 'OpenGraph Image',
      severity: 'pass',
      message: `Primary media attached (${primaryImage.mimeType}).`,
    });
  }

  // 4. Schema.org JSON-LD Offer Validation
  const hasVariantsWithPrice = variants.some((v) => v.basePriceMinor && parseInt(v.basePriceMinor, 10) > 0);
  if (!hasVariantsWithPrice) {
    rules.push({
      id: 'jsonld-price-missing',
      label: 'Schema.org Offers',
      severity: 'warning',
      message: 'No variants have valid pricing configured for Schema.org Offer microdata.',
    });
  } else {
    rules.push({
      id: 'jsonld-offers-pass',
      label: 'Schema.org Structured Data',
      severity: 'pass',
      message: 'Product Offer and Availability microdata verified.',
    });
  }

  return rules;
}

export const SeoDiagnosticsCard: React.FC<SeoDiagnosticsCardProps> = ({
  product,
  variants,
  media,
  locale,
}) => {
  const diagnostics = evaluateSeoDiagnostics(product, variants, media, locale);

  const titleText =
    typeof product.title === 'string'
      ? product.title
      : product.title?.[locale] || Object.values(product.title || {})[0] || 'Untitled Product';

  const descText =
    typeof product.description === 'string'
      ? product.description
      : product.description?.[locale] || Object.values(product.description || {})[0] || 'No description provided.';

  const slug = titleText.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);

  const severityBadges: Record<DiagnosticSeverity, { icon: string; color: string; bg: string }> = {
    pass: { icon: '✓', color: '#34d399', bg: 'rgba(52, 211, 153, 0.12)' },
    advisory: { icon: 'ℹ', color: '#38bdf8', bg: 'rgba(56, 189, 248, 0.12)' },
    warning: { icon: '⚠️', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.12)' },
    error: { icon: '✕', color: '#f87171', bg: 'rgba(248, 113, 113, 0.12)' },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Google Search Simulation Card */}
      <div
        style={{
          padding: '16px',
          borderRadius: '10px',
          backgroundColor: '#202124',
          border: '1px solid #3c4043',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <div style={{ fontSize: '11px', color: '#9aa0a6', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ backgroundColor: '#3c4043', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>🌐</span>
          <span>https://store.mesh.local › products › {slug || 'item'}</span>
        </div>
        <div style={{ fontSize: '16px', fontWeight: 500, color: '#8ab4f8', lineHeight: '1.3', cursor: 'pointer' }}>
          {titleText} - Sovereign Merchant Store
        </div>
        <div style={{ fontSize: '12px', color: '#bdc1c6', marginTop: '4px', lineHeight: '1.4' }}>
          {descText.slice(0, 160)}
          {descText.length > 160 ? '...' : ''}
        </div>
      </div>

      {/* Diagnostic Checklist */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase' }}>
          SEO & Structured Metadata Linter
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '6px' }}>
          {diagnostics.map((d) => {
            const badge = severityBadges[d.severity];
            return (
              <div
                key={d.id}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: badge.bg,
                  border: `1px solid ${badge.color}33`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '12px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: 800, color: badge.color }}>{badge.icon}</span>
                  <span style={{ fontWeight: 600, color: '#f8fafc' }}>{d.label}:</span>
                  <span style={{ color: 'var(--theme-text-secondary, #94a3b8)' }}>{d.message}</span>
                </div>
                {d.actualValue && (
                  <span style={{ fontSize: '11px', fontWeight: 700, color: badge.color }}>
                    {d.actualValue}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
